// config/carto.us.ts
//
// Configuration CARTO pour la section "US RAA Story" — reprend le même
// pattern que config/carto.ts (India) mais pointe vers la table US fournie:
//
//   carto-dw-ac-kc6q03le.shared_us.us_hex_table_2025_05_filtered
//
// Schéma réel de la table (confirmé) — colonnes utiles à ce projet:
//   tokeep                                 string   ("true"/"false")
//   airport_iata_catchment                 string   IATA de l'aéroport d'origine (catchment)
//   airport_city_catchment                 string   ville de l'aéroport d'origine
//   city_state_catchment                   string
//   state_catchment                        string
//   h3_destination                         string   index H3 de l'hexagone destination
//   distance_airport_h3_destination_nm     number   distance aéroport -> hex (NM)
//   total_weekly_trips                     number   trips hebdomadaires tous modes
//   total_trips_calibrated_weighted        number   trips annuels calibrés (tous modes)
//   percentage_of_trips_by_air             string
//   o_metropolitan_area_clean              string   zone métro d'origine (nettoyée)
//   h3_cent_latitude / h3_cent_longitude   string   centroïde de l'hexagone (fallback si h3-js échoue)
//   h3_city_dest / h3_state_code_dest      string   ville / état de l'hexagone destination
//   airport_iata_destination               string

import { CARTO_API, CARTO_TOKEN } from "./carto";

export { CARTO_API, CARTO_TOKEN };

// ─── Connexion pour la table "atr.usa.us_routes_table_evolution..." ──
// IMPORTANT : "atr" n'est PAS un projet BigQuery — c'est un catalog
// Databricks, sur une connexion CARTO séparée ("cartodatabricks"), PAS
// la connexion BigQuery "carto_dw" utilisée par la table hex. C'est la
// vraie cause du 403 ("Access Denied... or perhaps it does not exist") :
// la requête partait vers le mauvais système (BigQuery au lieu de
// Databricks), où cette table n'existe évidemment pas.
// Le token a déjà les droits "Allow access to all sources" sur la
// connexion cartodatabricks (confirmé dans CARTO Workspace → Tokens →
// Grants) — on réutilise donc le même token, seul le nom de connexion
// dans l'URL change.
export const CARTO_TOKEN_ROUTES = CARTO_TOKEN;
export const CARTO_API_ROUTES =
  "https://gcp-europe-west1.api.carto.com/v3/sql/cartodatabricks/query";

  export const CARTO_API_FOCUS = "https://gcp-europe-west1.api.carto.com/v3/sql/cartodatabricks/query";
export const CARTO_TOKEN_FOCUS = "eyJhbGciOiJIUzI1NiJ9.eyJhIjoiYWNfa2M2cTAzbGUiLCJqdGkiOiJmNTlkMjEzNiJ9.9FPAfVVx6YWtX3kjFWfTV6Qt65gJW4Yuu2r0J-K9iSA";

export const TABLE_US_HEX =
  "carto-dw-ac-kc6q03le.shared_us.us_hex_table_2025_05_filtered";

export const TABLE_FOCUS_CITIES = "`atr`.`mappingdata`.`us_airports_visualisation_data_jan25`";

// Table d'évolution des routes US — projet BigQuery différent ("atr"),
// interrogée via CARTO_TOKEN_ROUTES/CARTO_API_ROUTES ci-dessus.
// Databricks/Spark SQL (contrairement à BigQuery) exige que CHAQUE partie
// d'un identifiant à trois niveaux soit quotée séparément — exactement
// comme dans la requête d'origine fournie :
//   `atr`.`usa`.`us_routes_table_evolution_oag2026_07`
// et PAS `atr.usa.us_routes_table_evolution_oag2026_07` (un seul backtick
// autour du chemin entier, qui est la convention BigQuery utilisée par
// erreur ici avant — Databricks interprète ça comme UN identifiant
// littéralement nommé avec des points dedans, qui n'existe pas).
export const TABLE_US_ROUTES = "`atr`.`usa`.`us_routes_table_evolution_oag2026_07`";

// Plage d'années disponible pour le slider "year_ref" — à ajuster si la
// table couvre une plage différente une fois connectée en direct.
export const ROUTES_YEAR_MIN = 2000;
export const ROUTES_YEAR_MAX = 2024;
export const ROUTES_YEAR_DEFAULT = 2024;

export const ROUTES_DISTANCE_MIN = 0;
export const ROUTES_DISTANCE_MAX = 5400; // valeur fixe donnée par le widget CARTO Builder


// ─── LES 15 "FOCUS CITIES" DE LA US RAA STORY ─────────────────
// (Middle America — cf. slide "Middle America can lead the way to regional revival")

export interface FocusCity {
  iata: string;
  city: string;
  state: string;
  lat: number;
  lng: number;
  hub: "M" | "S"; // classification FAA NPIAS de la ville elle-même
}

export const FOCUS_CITIES: FocusCity[] = [
  { iata: "MKE", city: "Milwaukee", state: "WI", lat: 43.0, lng: -87.9, hub: "M" },
  { iata: "IND", city: "Indianapolis", state: "IN", lat: 39.77, lng: -86.15, hub: "M" },
  { iata: "STL", city: "St. Louis", state: "MO", lat: 38.63, lng: -90.2, hub: "M" },
  { iata: "BNA", city: "Nashville", state: "TN", lat: 36.16, lng: -86.78, hub: "M" },
  { iata: "MEM", city: "Memphis", state: "TN", lat: 35.15, lng: -90.05, hub: "S" },
  { iata: "MSY", city: "New Orleans", state: "LA", lat: 29.95, lng: -90.07, hub: "M" },
  { iata: "CVG", city: "Cincinnati", state: "OH", lat: 39.1, lng: -84.5, hub: "M" },
  { iata: "CMH", city: "Columbus", state: "OH", lat: 39.96, lng: -82.99, hub: "M" },
  { iata: "CLE", city: "Cleveland", state: "OH", lat: 41.5, lng: -81.69, hub: "M" },
  { iata: "PIT", city: "Pittsburgh", state: "PA", lat: 40.44, lng: -79.99, hub: "M" },
  { iata: "RIC", city: "Richmond", state: "VA", lat: 37.54, lng: -77.46, hub: "M" },
  { iata: "RDU", city: "Raleigh", state: "NC", lat: 35.78, lng: -78.64, hub: "M" },
  { iata: "TYS", city: "Knoxville", state: "TN", lat: 35.96, lng: -83.92, hub: "S" },
  { iata: "BHM", city: "Birmingham", state: "AL", lat: 33.52, lng: -86.8, hub: "S" },
  { iata: "AVL", city: "Asheville", state: "NC", lat: 35.6, lng: -82.55, hub: "S" },
];

// ─── L HUBS (grands hubs FAA NPIAS) mentionnés dans la story ──

export interface LHub {
  iata: string;
  city: string;
  lat: number;
  lng: number;
}

export const L_HUBS: LHub[] = [
  { iata: "ATL", city: "Atlanta", lat: 33.75, lng: -84.39 },
  { iata: "ORD", city: "Chicago", lat: 41.88, lng: -87.63 },
  { iata: "IAD", city: "Washington Dulles", lat: 38.95, lng: -77.46 },
  { iata: "CLT", city: "Charlotte", lat: 35.23, lng: -80.84 },
  { iata: "DFW", city: "Dallas–Fort Worth", lat: 32.9, lng: -97.04 },
  { iata: "DEN", city: "Denver", lat: 39.74, lng: -104.99 },
  { iata: "JFK", city: "New York JFK", lat: 40.64, lng: -73.78 },
];

// Catchments interrogeables. Le filtre demandé porte sur la colonne
// `airport_city_catchment` (nom de ville, ex. "Indianapolis") — PAS sur
// `airport_iata_catchment` (code IATA, ex. "IND"), même si ce sont deux
// colonnes distinctes du même schéma. On construit donc la liste à partir
// des noms de ville des 15 focus cities.
// ⚠️ À vérifier une fois connecté à la table réelle : si les valeurs de
// airport_city_catchment sont en fait au format "Indianapolis, IN" ou
// diffèrent légèrement, ajuster ce mapping en conséquence.
export const CATCHMENT_OPTIONS = FOCUS_CITIES.map((c) => c.city);
export const CATCHMENT_OPTIONS_IATA = FOCUS_CITIES.map((c) => c.iata);

// Distance par défaut (NM) — reprend le seuil "under 400NM" de la story
export const DEFAULT_GCD_NM_FROM = 0;
export const DEFAULT_GCD_NM_TO = 400;
export const GCD_NM_MAX = 1200;

// Centre par défaut de la caméra 3D (Middle America)
export const US_MAP_CENTER_LAT = 38.5;
export const US_MAP_CENTER_LNG = -87.5;
